import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import DOMPurify from "dompurify";

const svgCache = {}; // Global cache for raw (unmodified) SVGs

const DynamicIcon = ({
  iconURL,
  handleIconClick,
  color = "var(--secondary-theme-color)",
  height: propHeight,
  width: propWidth,
}) => {
  const height = propHeight ?? 24;
  const width = propWidth ?? 24;

  const cacheKey = useMemo(
    () => `${iconURL}_${width}x${height}`,
    [iconURL, width, height]
  );
  const [rawSvg, setRawSvg] = useState(svgCache[cacheKey] || "");
  const isFetching = useRef(false);

  const fetchSvg = useCallback(async () => {
    if (svgCache[cacheKey]) {
      setRawSvg(svgCache[cacheKey]); // Use cached version
      return;
    }

    if (!isFetching.current) {
      isFetching.current = true;
      try {
        const res = await fetch(iconURL);
        const data = await res.text();

        const parser = new DOMParser();
        const doc = parser.parseFromString(data, "image/svg+xml");
        const svg = doc.querySelector("svg");

        if (svg) {
          // Ensure width and height are set
          svg.setAttribute("width", width);
          svg.setAttribute("height", height);

          // Sanitize the SVG to remove scripts and unsafe attributes
          const sanitizedSvg = DOMPurify.sanitize(svg.outerHTML, {
            USE_PROFILES: { svg: true },
          });

          svgCache[cacheKey] = sanitizedSvg; // Store sanitized SVG with dimensions
          setRawSvg(sanitizedSvg);
        }
      } finally {
        isFetching.current = false;
      }
    }
  }, [cacheKey, iconURL, width, height]); // Ensure effect reruns on dimension

  useEffect(() => {
    fetchSvg();
  }, [fetchSvg]);

  const coloredSvg = useMemo(() => {
    if (!rawSvg) return "";
    return rawSvg.replace(/fill="(?!none|transparent).*?"/g, `fill="${color}"`);
  }, [rawSvg, color]);

  return (
    <>
      {rawSvg ? (
        <div
          onClick={handleIconClick}
          className="cursor-pointer"
          dangerouslySetInnerHTML={{ __html: coloredSvg }} // Now dynamically applies color
        />
      ) : (
        <div
          style={{ width, height, background: "#f0f0f0" }} // Placeholder while loading
        />
      )}
    </>
  );
};

export default DynamicIcon;
